import React, { useContext, createContext, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import localPersistenceHelper from 'helpers/localPersistenceHelper.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import { useFirebase } from '../components/Firebase/index.js';

export const FeatureContext = createContext({
  features: {},
  loading: false,
}); // object should be what features are enabled for the org
FeatureContext.displayName = 'FeatureContext'; // React devtools display name

const FEATURES_LOCAL_STORAGE_PREFIX = '';
const FEATURES_LOCAL_STORAGE_KEY = 'features';

export const getLocalFeatures = () => {
  // Local meaning from locale storage and query params, local on your device
  let features = localPersistenceHelper.getItem({
    key: FEATURES_LOCAL_STORAGE_KEY,
    prefix: FEATURES_LOCAL_STORAGE_PREFIX,
  }) || {};
  const queryParamName = 'features';
  const url = window.location.search.replace(/\%5B/g, '[').replace(/\%5D/g, ']');
  const testStr = `[?&]${queryParamName}(\\[(.*?)\\])?(=([^&#]*)|&|#|$)`;
  const matches = [...url.matchAll(testStr)];
  if (matches.length === 1 && !matches[0][2]) {
    const featuresStr = decodeURIComponent((matches[0][4] || '').replace(/\+/g, ' '));
    if (featuresStr === 'false' || featuresStr === 'clear') {
      features = {};
    } else {
      featuresStr.split(',').forEach(feature => {
        const [queryKey, value = 'true'] = feature.split('=');
        features[queryKey] = value === 'true' ? true : false;
      });
    }
    localPersistenceHelper.setItem({
      key: FEATURES_LOCAL_STORAGE_KEY,
      data: features,
      prefix: FEATURES_LOCAL_STORAGE_PREFIX,
    });
  }

  return features;
};

const localFeatures = getLocalFeatures();

export const getFeaturesQueryKey = (uid, queryString) => ['features', uid, queryString];

const ContextProvider = (props) => {
  const { orgId } = useProductDataOutsideRoutes();
  const firebase = useFirebase();
  const uid = useSelector(state => state.sessionState.authUser?.uid);

  const queryString = new URLSearchParams({
    requestClient: 'lvsDashboard',
    ...(orgId && { orgId }),
  }).toString();

  const queryKey = useMemo(() => getFeaturesQueryKey(uid, queryString), [uid, queryString]);

  const { data: orgFeatures, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const agent = await firebase.getAgent();
      try {
        const response = await agent.get(`/features?${queryString}`);
        if (response.status !== 200) throw new Error('Error fetching features');
        return Object.keys(response.body.features).reduce((acc, key) => {
          acc[key] = !!response.body.features[key].enabled;
          return acc;
        }, {});
      } catch (error) {
        return {};
      }
    },
    enabled: true,
  });

  const features = useMemo(
    () =>
      Object.entries({ ...orgFeatures, ...localFeatures }).reduce((acc, [key, feature]) => {
        acc[`${key}Enabled`] = feature;
        return acc;
      }, {}),
    [orgFeatures],
  );

  const context = {
    features,
    loading: isLoading,
  };

  return (
    <FeatureContext.Provider value={context}>
      {props.children}
    </FeatureContext.Provider>
  );
};

export const clearLocalFeatures = () => {
  localPersistenceHelper.removeItem({
    key: FEATURES_LOCAL_STORAGE_KEY,
    prefix: FEATURES_LOCAL_STORAGE_PREFIX,
  });
};

export const FeatureContextProvider = withRouter(ContextProvider);

export const useFeatureContext = () => useContext(FeatureContext);

export const withFeatures = Component => (props) => {
  const featureContext = useFeatureContext();
  return (
    <Component {...props} featureContext={featureContext} />
  );
};
